import React from "react";
import styled from "styled-components";
import { Input } from "@shared/ui/Input";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { HorizontalFormField } from "@shared/ui/HorizontalFormField";
import { RadioButton } from "@shared/ui/RadioButton";
import { Select } from "@shared/ui/Select";
import {
  IApplicationForm,
  IApplicationFormStep4,
} from "@entities/Product/Application/types";
import { extractNumbers, setSpacesInNumber } from "@shared/lib";
import { findOption } from "@entities/Product/Application/utils";
import { ISelectOption } from "@shared/types";
import { PatternFormat } from "react-number-format";

interface IProps {
  classificationOptions: { value: string; label: string }[];
}

export function Step4Fields({ classificationOptions }: IProps) {
  const { t } = useTranslation();
  const methods = useFormContext<IApplicationFormStep4 | IApplicationForm>();

  return (
    <>
      <HorizontalFormField label={t("Название компании")}>
        <Controller
          name="company_name"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              hasError={Boolean(fieldState.error?.message)}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Вид деятельности")}>
        <Controller
          control={methods.control}
          name="classification"
          render={({ field, fieldState }) => (
            <>
              <StyledRadioGroup>
                {classificationOptions.map((item) => (
                  <RadioButton
                    {...field}
                    onChange={() => field.onChange(item.value)}
                    key={item.value}
                    id={item.value}
                    value={item.value}
                    label={item.label}
                    checked={item?.value === field.value}
                  />
                ))}
              </StyledRadioGroup>
              <StyledSelect
                value={findOption(classificationOptions, field.value)}
                onChange={(newValue: ISelectOption) =>
                  field.onChange(newValue.value)
                }
                hasError={Boolean(fieldState.error)}
                errorMessage={fieldState.error?.message}
                options={classificationOptions}
                placeholder={t(`Вид деятельности`)}
                noOptionsMessage={() => t(`Не найдено`)}
              />
            </>
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Должность")}>
        <Controller
          name="position"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input {...field} hasError={Boolean(fieldState.error?.message)} />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Общий период работы(месяцы)")}>
        <Controller
          name="working_period"
          control={methods.control}
          render={({ field, fieldState }) => (
            <PatternFormat
              {...field}
              hasError={Boolean(fieldState.error?.message)}
              format="##.##.####"
              mask="_"
              allowEmptyFormatting
              type="text"
              customInput={Input}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Месячный доход")}>
        <Controller
          name="month_income"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              onChange={(e) =>
                field.onChange(
                  setSpacesInNumber(extractNumbers(e.target.value))
                )
              }
              inputMode="numeric"
              hasError={Boolean(fieldState.error?.message)}
            />
          )}
        />
      </HorizontalFormField>
    </>
  );
}

const StyledRadioGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-gap: 1.3rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    display: none;
  }
`;

const StyledSelect = styled(Select)`
  display: none;
  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    display: block;
  }
`;
